import React, { useEffect, useState } from "react";

import Image from "next/image";
import { useTranslation } from "react-i18next";

interface GailLoaderProps {
  messages?: string[];
  typingSpeed?: number;
  deletingSpeed?: number;
  pauseDuration?: number;
}

// Animation phases
enum AnimationPhase {
  TYPING,
  PAUSING,
  DELETING,
}

const GailLoader: React.FC<GailLoaderProps> = ({
  messages,
  typingSpeed = 50,
  deletingSpeed = 30,
  pauseDuration = 500,
}) => {
  const { t } = useTranslation();

  const defaultMessages = [
    t("common.gailLoader.messages.gettingEverythingReady"),
    t("common.gailLoader.messages.loadingYourAssistant"),
    t("common.gailLoader.messages.preparingAIModels"),
    t("common.gailLoader.messages.almostThere"),
  ];

  const displayMessages = messages || defaultMessages;

  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState("");
  const [phase, setPhase] = useState<AnimationPhase>(AnimationPhase.TYPING);

  const currentMessage = displayMessages[currentMessageIndex];

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    switch (phase) {
      case AnimationPhase.TYPING:
        if (displayedText.length < currentMessage.length) {
          // Continue typing
          timeout = setTimeout(() => {
            setDisplayedText(currentMessage.slice(0, displayedText.length + 1));
          }, typingSpeed);
        } else {
          // Message fully typed, start pause
          timeout = setTimeout(() => {
            setPhase(AnimationPhase.PAUSING);
          }, pauseDuration);
        }
        break;

      case AnimationPhase.PAUSING:
        // Pause completed, start deleting
        timeout = setTimeout(() => {
          setPhase(AnimationPhase.DELETING);
        }, pauseDuration);
        break;

      case AnimationPhase.DELETING:
        if (displayedText.length > 0) {
          // Continue deleting
          timeout = setTimeout(() => {
            setDisplayedText(displayedText.slice(0, -1));
          }, deletingSpeed);
        } else {
          // Message fully deleted, move to next message and start typing
          setCurrentMessageIndex((prev) => (prev + 1) % displayMessages.length);
          setPhase(AnimationPhase.TYPING);
        }
        break;
    }

    return () => clearTimeout(timeout);
  }, [
    displayedText,
    phase,
    currentMessage,
    currentMessageIndex,
    displayMessages,
    typingSpeed,
    deletingSpeed,
    pauseDuration,
  ]);

  // Blinking cursor effect
  const [cursorVisible, setCursorVisible] = useState(true);

  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setCursorVisible((prev) => !prev);
    }, 500);

    return () => clearInterval(cursorInterval);
  }, []);

  return (
    <div className="flex flex-col items-center justify-center h-full font-sans">
      <div className="flex items-center mb-2">
        <Image
          src="/images/gail_icon_black.svg"
          alt="Gail Icon"
          width={32}
          height={32}
          className="mr-2 h-8 w-8"
        />
      </div>
      <div className="text-sm text-black mt-1 flex min-h-6 items-center justify-center">
        <span className="inline-block">{displayedText}</span>
        <span
          className={`inline-block border-r-2 border-gray-500 h-4 ${
            cursorVisible ? "opacity-100" : "opacity-0"
          }`}
        ></span>
      </div>
    </div>
  );
};

export default GailLoader;
